import { Component, createSignal, onMount, For, Show, createEffect } from 'solid-js';
import { useLanguage } from '../i18n/LanguageContext';
import { useNavigation } from '../contexts/NavigationContext';

interface BlogPostTranslation {
  slug: string;
  title: string;
  excerpt: string;
}

interface BlogPost {
  id: string;
  date: string;
  translations: Record<string, BlogPostTranslation>;
}

interface DisplayPost {
  slug: string;
  title: string;
  date: string;
  excerpt: string;
}

const BlogListPage: Component = () => {
  const { t, language } = useLanguage();
  const { navigateToArticle } = useNavigation();
  const [allPosts, setAllPosts] = createSignal<BlogPost[]>([]);
  const [displayPosts, setDisplayPosts] = createSignal<DisplayPost[]>([]);
  const [loading, setLoading] = createSignal(true);

  onMount(async () => {
    try {
      const response = await fetch('/blog/index.json');
      if (response.ok) {
        const data = await response.json();
        setAllPosts(data.posts || []);
      }
    } catch (error) {
      console.error('Error loading blog posts:', error);
    } finally {
      setLoading(false);
    }
  });

  // Filter posts by language
  createEffect(() => {
    const lang = language();
    const posts = allPosts();

    const filtered = posts
      .filter(post => post.translations[lang])
      .map(post => {
        const translation = post.translations[lang];
        return {
          slug: translation.slug,
          title: translation.title,
          date: post.date,
          excerpt: translation.excerpt
        };
      });

    setDisplayPosts(filtered);
  });

  const handleArticleClick = (slug: string) => (e: MouseEvent) => {
    e.preventDefault();
    console.log('Article clicked, slug:', slug);
    navigateToArticle(slug);
    console.log('navigateToArticle called');
  };

  return (
    <section class="blog-page">
      <div class="container">
        <h1 class="page-title">{t('blog.title')}</h1>

        <Show when={loading()}>
          <div class="blog-loading">{t('blog.loading')}</div>
        </Show>

        <Show when={!loading() && displayPosts().length === 0}>
          <div class="blog-empty">
            <p>{t('blog.empty')}</p>
          </div>
        </Show>

        <Show when={!loading() && displayPosts().length > 0}>
          <div class="blog-grid">
            <For each={displayPosts()}>
              {(post) => (
                <article class="blog-card">
                  <div class="blog-card-header">
                    <h2 class="blog-card-title">
                      <a href="#" onClick={handleArticleClick(post.slug)}>{post.title}</a>
                    </h2>
                    <time class="blog-card-date">{post.date}</time>
                  </div>
                  <p class="blog-card-excerpt">{post.excerpt}</p>
                  <a href="#" onClick={handleArticleClick(post.slug)} class="blog-card-link">
                    {t('blog.readMore')} →
                  </a>
                </article>
              )}
            </For>
          </div>
        </Show>
      </div>
    </section>
  );
};

export default BlogListPage;
